import { useCallback, useRef } from "react";
import { toast } from "sonner";

/**
 * Soft-delete with 5-second Undo — the inverse pattern of `useConfirm()`.
 *
 * Why this exists:
 *   "Are you sure?" modals interrupt every destructive action, even routine
 *   ones (discard a draft, archive a thread, delete a person). Users learn to
 *   click-through reflexively, which defeats the safety. Optimistic + undoable
 *   is the modern pattern: the UI updates instantly, the server call is
 *   deferred for ~5 seconds, and a toast offers Undo. The user gets fewer
 *   clicks AND a real safety net.
 *
 * Usage:
 *   const undoable = useUndoable();
 *   undoable({
 *     message: "Draft discarded",
 *     description: "Will permanently delete in 5 s",
 *     delayMs: 5000,
 *     optimisticApply: () => setRows((r) => r.filter((x) => x.id !== id)),
 *     perform:        () => api.delete(`/warranty/claims/${id}`),
 *     revert:         () => loadRows(),  // re-fetch on undo OR perform-failure
 *     onComplete:     () => toast.success("Draft permanently deleted."),
 *     onUndo:         () => toast.info("Restored."),
 *     testId:         "warranty-discard-undo",
 *   });
 *
 * The hook is a thin orchestrator — it owns the timer, the Undo button,
 * and the rollback dance, but never the data. All optimistic / revert
 * logic lives in the calling component (which knows its own state best).
 *
 * Failure modes:
 *   • perform() throws → revert() is called and toast.error is shown.
 *   • User clicks Undo before the timer fires → timer cleared, revert()
 *     called, perform() never runs, onUndo() called.
 *   • User triggers a 2nd undoable while the 1st is still pending: the 1st
 *     fires its perform() immediately (no double-pending dance), keeping
 *     the UI deterministic.
 */
export function useUndoable() {
  // Single in-flight timer. Tracking by ref so the imperative caller never
  // re-renders this hook's owner.
  const pendingRef = useRef(null);

  const flushPending = useCallback(async () => {
    const p = pendingRef.current;
    if (!p) return;
    pendingRef.current = null;
    clearTimeout(p.timer);
    try {
      await p.perform();
      p.onComplete?.();
    } catch (e) {
      // Roll back the optimistic UI and surface the error.
      try { p.revert?.(); } catch { /* ignore */ }
      const msg = e?.response?.data?.detail || e?.message || "Action failed";
      toast.error(msg);
    }
  }, []);

  const cancelPending = useCallback(() => {
    const p = pendingRef.current;
    if (!p) return;
    pendingRef.current = null;
    clearTimeout(p.timer);
    try { p.revert?.(); } catch { /* ignore */ }
    p.onUndo?.();
  }, []);

  const start = useCallback((opts = {}) => {
    const {
      message = "Action queued",
      description = "",
      delayMs = 5000,
      undoLabel = "Undo",
      optimisticApply,
      perform,
      revert,
      onComplete,
      onUndo,
      testId = "undoable-toast",
    } = opts;

    if (typeof perform !== "function") {
      throw new Error("useUndoable: `perform` is required");
    }

    // If there's already a pending action, flush it right now so we don't
    // stack overlapping undo windows. Common case: user discards 3 drafts
    // in a row — each one resolves in order, in <5s, with a fresh toast.
    if (pendingRef.current) flushPending();

    // 1. Apply the optimistic update immediately.
    try { optimisticApply?.(); } catch (_) { /* component decides */ }

    // 2. Schedule the real action.
    const id = Math.random().toString(36).slice(2, 8);
    const timer = setTimeout(() => { flushPending(); }, Math.max(500, delayMs));
    pendingRef.current = { id, timer, perform, revert, onComplete, onUndo };

    // 3. Toast with Undo. Sonner's `duration` is intentionally a hair longer
    //    than delayMs so the button stays visible until the timer fires —
    //    after that the action is permanent and the Undo would be a lie.
    toast(message, {
      description,
      duration: delayMs + 200,
      action: {
        label: undoLabel,
        onClick: () => {
          // Only undo if THIS toast's pending hasn't already been flushed
          // (race: user clicks Undo while the timer is firing).
          if (pendingRef.current?.id === id) cancelPending();
        },
      },
      // Tag the toast for tests + visual styling.
      className: "undoable-toast",
      // The id keeps repeated identical messages from stacking visually.
      id: `${testId}-${id}`,
    });
  }, [flushPending, cancelPending]);

  return start;
}
